// src/pages/ForgotPassword/ForgotPassword.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, AlertCircle, CheckCircle, ArrowLeft } from "lucide-react";
import "./ForgotPassword.css";
import AuthLayout from "../../components/AuthLayout/AuthLayout";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const res = await fetch(`${API_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || "Failed to send reset email");
      }

      setSuccess(true);
      setEmail(""); // Clear email field
    } catch (err) {
      if (err.name === "TypeError" && err.message.includes("fetch")) {
        setError("Cannot connect to server. Please check your internet connection.");
      } else {
        setError(err.message);
      }
      console.error("Forgot password error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
    <div className="forgot-password-container">
      <div className="forgot-password-card">
        {!success ? (
          <>
            <div className="icon-wrapper">
              <Mail size={48} className="mail-icon" />
            </div>
            
            <h2>Forgot Password?</h2>
            <p className="subtitle">
              No worries! Enter your email address and we'll send you a link to reset your password.
            </p>

            {error && (
              <div className="error-banner">
                <AlertCircle size={18} />
                <span>{error}</span>
              </div>
            )}

            <form className="forgot-password-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Email Address</label>
                <div className="input-wrapper">
                  <Mail size={20} className="input-icon" />
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError("");
                    }}
                    disabled={loading}
                    required
                    autoComplete="email"
                    className={error ? "input-error with-icon" : "with-icon"}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !email}
                className={loading ? "loading" : ""}
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
            </form>

            <div className="back-to-login">
              <Link to="/login">
                <ArrowLeft size={16} />
                <span>Back to Login</span>
              </Link>
            </div>
          </>
        ) : (
          <div className="success-state">
            <div className="success-icon-wrapper">
              <CheckCircle size={64} className="success-icon" />
            </div>
            
            <h2>Check Your Email!</h2>
            <p className="success-message">
              If an account exists with that email, we've sent password reset instructions.
            </p>
            <p className="info-text">
              Please check your inbox and spam folder. The link will expire in 1 hour.
            </p>

            <div className="success-actions">
              <Link to="/login" className="back-to-login-btn">
                Back to Login
              </Link>
              <button
                onClick={() => setSuccess(false)}
                className="try-again-btn"
              >
                Try Another Email
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </AuthLayout>
  );
};

export default ForgotPassword;